import { OrderLineItemV2 } from '@portal/state/internal/getOrderDetails/types';
import { SubscriptionLineItemV2 } from '@portal/state/internal/getSubscriptionDetails/types';
import { CartLine } from 'api/shopifyV2/types';
import clsx from 'clsx';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { getFlavorConfigFromVariantId } from '../../../constants/flavors';
import { useStoreValue } from '../../../state/hooks/useStore';
import { useStoreCurrency } from '../../../state/hooks/useStoreCurrency';
import { isPreferredAGZFlavor, isV53Product } from '../../components/V53EditProductsModal/utils';
import { useMoney } from '../../hooks/useMoney';
import { ProductDetails } from '../ProductDetails/ProductDetails';
import { ProductImage } from '../ProductImage/ProductImage';
import styles from './ProductLineItem.module.scss';

type ProductItemType = OrderLineItemV2 | SubscriptionLineItemV2 | CartLine;

interface ExtractedProductData {
  name?: string;
  imageUrl?: string;
  variantTitle?: string;
  variantId?: string;
  quantity: number;
  isCartLine: boolean;
  isOneTime: boolean;
  currency?: string;
}

const extractProductData = (item: ProductItemType): ExtractedProductData => {
  const isCartLine = 'merchandise' in item;

  if (isCartLine) {
    const cartItem = item as CartLine;
    const isOneTime = !cartItem.sellingPlanAllocation;

    return {
      name: item.merchandise.product.title,
      imageUrl: item.merchandise.image.url,
      variantTitle: item.merchandise.title,
      variantId: item.merchandise.id,
      quantity: item.quantity,
      isCartLine,
      isOneTime,
      currency: cartItem.cost.totalAmount.currencyCode
    };
  }
  // Assuming OrderLineItemV2 or SubscriptionLineItemV2
  return {
    name: item.product?.name,
    imageUrl: item.product?.image_url,
    variantTitle: item.product?.shopify_variant_title,
    variantId: item.product?.shopify_variant_id,
    quantity: item.quantity,
    isCartLine,
    isOneTime: item.is_one_time,
    currency: undefined
  };
};

interface CalculatedPrices {
  finalPrice: number;
  originalPriceBeforeDiscount: number;
  discountValue: number;
  hasDiscount: boolean;
  isFree: boolean;
}

const calculateProductPrices = (item: ProductItemType, isCartLine: boolean): CalculatedPrices => {
  let finalPrice: number;
  let originalPriceBeforeDiscount: number;
  let discountValue: number;

  if (isCartLine) {
    const cartItem = item as CartLine;

    finalPrice = parseFloat(cartItem.cost.totalAmount.amount || '0');
    originalPriceBeforeDiscount = parseFloat(cartItem.cost.subtotalAmount.amount || '0');
    discountValue = originalPriceBeforeDiscount - finalPrice;
  } else {
    const orderOrSubItem = item as OrderLineItemV2 | SubscriptionLineItemV2;
    finalPrice = orderOrSubItem.total_amount;
    discountValue = orderOrSubItem.discount_amount;
    originalPriceBeforeDiscount = finalPrice + discountValue;
  }

  const hasDiscount = discountValue > 0 && !isCartLine; // Only show discount strikethrough for non-cart items
  const isFree = finalPrice === 0;

  return {
    finalPrice,
    originalPriceBeforeDiscount,
    discountValue,
    hasDiscount,
    isFree
  };
};

interface ProductItemProps {
  item: ProductItemType;
  hideSubtitle?: boolean;
  className?: string;
  quantityEditor?: ReactNode;
  isProcessing?: boolean;
  currencyCode?: string;
}

export const ProductLineItem = ({
  item,
  className,
  hideSubtitle,
  quantityEditor,
  isProcessing,
  currencyCode
}: ProductItemProps) => {
  const { currency: storeCurrency } = useStoreCurrency();
  const store = useStoreValue();
  const { t } = useTranslation('subscriptionDetails');
  const { formatMoney } = useMoney();

  // Extract data using helper
  const { name, imageUrl, variantTitle, variantId, quantity, isCartLine, isOneTime, currency } =
    extractProductData(item);

  // Calculate prices using helper
  const { finalPrice, originalPriceBeforeDiscount, hasDiscount, isFree } = calculateProductPrices(
    item,
    isCartLine
  );

  const getDataTestId = () => {
    if (isCartLine) {
      return 'cart-product';
    }
    return isOneTime ? 'order-item' : 'subscription-item';
  };

  const imageAlt = name || 'Product image';
  const title = name || 'Product';
  const subtitle = hideSubtitle || variantTitle?.includes('Default') ? undefined : variantTitle;
  const flavorInfo = variantId ? getFlavorConfigFromVariantId(variantId, store) : undefined;

  // Use item-specific currency when available, fall back to store currency
  const currencyToUse = currency || currencyCode || storeCurrency;

  const formattedFinalPrice = isFree ? t('active.free') : formatMoney(finalPrice, currencyToUse);

  const formattedOriginalPrice = hasDiscount
    ? formatMoney(originalPriceBeforeDiscount, currencyToUse)
    : undefined;

  if (!imageUrl) {
    return null;
  }

  return (
    <div className={clsx(styles.productItemWrapper, className)} data-testid={getDataTestId()}>
      <ProductImage src={imageUrl} alt={imageAlt} quantity={quantity} />
      <ProductDetails
        title={title}
        subtitle={subtitle}
        flavor={flavorInfo}
        discountedPriceText={formattedFinalPrice}
        originalPriceText={formattedOriginalPrice}
        hasDiscount={hasDiscount}
        showUpgradedFormula={variantId ? isV53Product(variantId, store) : false}
        showPreferredFlavor={isPreferredAGZFlavor(store, variantId)}
        quantityEditor={quantityEditor}
        isProcessing={isProcessing}
      />
    </div>
  );
};
